class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.headerWrapper = document.querySelector('.header-wrapper.round-header');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // Escape regex special characters so user input like "case (black)" doesn't break the RegExp constructor
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getPredictiveSearchUrls(searchTerm) {
    const params = `?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    const urls = [];
    const primary = `${routes.predictive_search_url}${params}`;
    urls.push(primary);

    ['/search/suggest', '/en/search/suggest'].forEach((path) => {
      const fallback = `${path}${params}`;
      if (!urls.includes(fallback) && routes.predictive_search_url !== path) {
        urls.push(fallback);
      }
    });

    return urls;
  }

  async fetchPredictiveSearchMarkup(searchTerm, signal) {
    let lastError = null;

    for (const url of this.getPredictiveSearchUrls(searchTerm)) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const response = await fetch(url, { signal });
      if (!response.ok) {
        lastError = new Error(String(response.status));
        if (response.status === 417 || response.status === 404) continue;
        break;
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')?.innerHTML;

      if (resultsMarkup) return resultsMarkup;
    }

    throw lastError || new Error('Predictive search failed');
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.fetchPredictiveSearchMarkup(searchTerm, signal)
      .then((resultsMarkup) => {
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.renderFallbackResults(searchTerm);
      });
  }

  renderFallbackResults(searchTerm) {
    const template = this.getAttribute('data-search-for') || searchTerm;
    const label = template.replaceAll('[terms]', searchTerm);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div id="predictive-search-results" role="listbox">
        <div class="predictive-search__search-for-button">
          <button type="submit" class="predictive-search__item predictive-search__item--term link link--text h5" tabindex="0" role="option" aria-selected="false">
            <span data-predictive-search-search-for-text></span>
          </button>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value></span>
    `;
    const textEl = wrapper.querySelector('[data-predictive-search-search-for-text]');
    if (textEl) textEl.textContent = label;
    this.renderSearchResults(wrapper.innerHTML);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    this.querySelector('.predictive-search__loading-state')?.classList.remove('hidden');
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Avoid duplicate global IDs when multiple header search instances are present
    this.predictiveSearchResults.querySelector('#predictive-search-results')?.removeAttribute('id');

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.querySelector('.predictive-search__loading-state')?.classList.add('hidden');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) this.setLiveRegionText(liveRegionCount.textContent);
  }

  getResultsMaxHeight() {

    if (this.headerWrapper) {
      this.resultsMaxHeight =
        window.innerHeight - this.headerWrapper?.getBoundingClientRect().bottom - 70;
    } else {
      this.resultsMaxHeight =
        window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    }

    return this.resultsMaxHeight;
  }

  isHeaderSearch() {
    return !!this.closest('.header__search');
  }

  isSearchModalOpen() {
    return !!this.closest('details-modal')?.querySelector('details[open]');
  }

  lockBodyScroll() {
    if (!this.isHeaderSearch()) return;
    document.body.classList.add('overflow-hidden');
  }

  unlockBodyScroll() {
    if (!this.isHeaderSearch()) return;
    if (this.isSearchModalOpen()) return;

    const hasOpenHeaderPredictiveSearch = document.querySelector('.header__search predictive-search[open]');
    if (!hasOpenHeaderPredictiveSearch) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.lockBodyScroll();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.querySelector('.predictive-search__loading-state')?.classList.add('hidden');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.unlockBodyScroll();
  }
}

customElements.define('predictive-search', PredictiveSearch);